// Trang Góp ý & Phản hồi từ người dùng (Shop, Đại lý, CTV, Nhân viên).
// Cho phép gửi góp ý phân loại, đánh giá sao, và xem lịch sử các phản hồi đã gửi.

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/ui/page-header'
import { FilterTabs } from '@/components/ui/filter-tabs'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Star, CheckCircle2 } from 'lucide-react'
import { post, get } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { IndexBadge } from '@/components/ui/data-table'
import { formatDateTime } from '@/lib/format'
import { canHandleDownline, resolveFeedbackTab, TICKET_STATUS_LABEL, type TicketStatus } from '@shoptrack/common/tickets'
import { TicketHandling, type TicketHandlingFields } from '@/components/feedback/TicketHandling'
import { DownlineTickets } from '@/components/feedback/DownlineTickets'

type Category = 'bug' | 'feature' | 'ui_ux' | 'other'

interface FeedbackItem extends TicketHandlingFields {
  id: string
  category: Category
  title: string
  content: string
  rating?: number | null
  status: TicketStatus
  createdAt: string
}

const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'bug', label: 'Báo lỗi' },
  { id: 'feature', label: 'Đề xuất tính năng' },
  { id: 'ui_ux', label: 'Giao diện & Trải nghiệm' },
  { id: 'other', label: 'Khác' },
]

const STAR_LABELS: Record<number, string> = {
  1: 'Rất không hài lòng',
  2: 'Chưa hài lòng',
  3: 'Bình thường',
  4: 'Hài lòng',
  5: 'Rất hài lòng',
}

export default function FeedbackPage() {
  const queryClient = useQueryClient()
  const auth = getAuthState()
  const currentShop = auth.shops.find((s) => s.shopId === auth.activeShopId)

  // Tab trên URL (?tab=history|downline&id=…) — thông báo mở thẳng đúng góp ý / ticket.
  const [params, setParams] = useSearchParams()
  const canDownline = canHandleDownline(currentShop?.tenantType, currentShop?.role)
  const { tab, downlineBlocked } = resolveFeedbackTab(params.get('tab'), canDownline)
  const focusId = params.get('id')
  const setTab = (v: 'form' | 'history' | 'downline') => setParams(v === 'form' ? {} : { tab: v }, { replace: true })
  const [category, setCategory] = useState<Category>('feature')
  const [rating, setRating] = useState<number>(5)
  const [hoverRating, setHoverRating] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [senderName, setSenderName] = useState(currentShop?.name || '')
  const [senderPhone, setSenderPhone] = useState(auth.phone || '')
  const [senderEmail, setSenderEmail] = useState(auth.email || '')

  // Lấy danh sách lịch sử phản hồi
  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['my-feedback'],
    queryFn: () => get<{ items: FeedbackItem[] }>('/feedback/my-feedback'),
    enabled: tab === 'history',
  })

  useEffect(() => {
    if (tab === 'history' && focusId && historyData) document.getElementById(`feedback-${focusId}`)?.scrollIntoView({ block: 'center' })
  }, [tab, focusId, historyData])

  // Mutation gửi phản hồi
  const submitMutation = useMutation({
    mutationFn: async (payload: {
      category: Category
      title: string
      content: string
      rating: number
      senderName?: string
      senderPhone?: string
      senderEmail?: string
      pageUrl?: string
    }) => {
      return post<{ ok: boolean; id: string; message: string }>('/feedback', payload)
    },
    onSuccess: (res) => {
      toast.success(res.message || 'Cảm ơn bạn đã đóng góp ý kiến!')
      setTitle('')
      setContent('')
      setRating(5)
      queryClient.invalidateQueries({ queryKey: ['my-feedback'] })
      setTab('history')
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Không thể gửi phản hồi, vui lòng thử lại sau.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) {
      toast.error('Vui lòng nhập tiêu đề góp ý')
      return
    }
    if (!content.trim() || content.trim().length < 5) {
      toast.error('Nội dung góp ý tối thiểu 5 ký tự')
      return
    }

    submitMutation.mutate({
      category,
      title: title.trim(),
      content: content.trim(),
      rating,
      senderName: senderName.trim() || undefined,
      senderPhone: senderPhone.trim() || undefined,
      senderEmail: senderEmail.trim() || undefined,
      pageUrl: window.location.href,
    })
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader title="Góp ý" />

      {downlineBlocked && (
        <p className="rounded-lg border border-warn/60 bg-warn/12 p-3 text-sm text-ink">
          Góp ý này thuộc CTV / Đại lý khác với đơn vị bạn đang chọn.
        </p>
      )}

      <FilterTabs
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        options={[
          { value: 'form', label: 'Gửi góp ý' },
          { value: 'history', label: 'Đã gửi' },
          ...(canDownline ? [{ value: 'downline', label: 'Góp ý cấp dưới' }] : []),
        ]}
      />

      {/* Main Content Area */}
      {tab === 'form' ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Card 1: Chọn phân loại */}
          <div className="rounded-xl bg-raise p-5 sm:p-6 space-y-4 shadow-card">
            <div>
              <h2 className="text-base font-semibold text-ink">Chủ đề</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CATEGORIES.map((item) => {
                const isSelected = category === item.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCategory(item.id)}
                    className={`motion-state flex items-start gap-3.5 rounded-xl border p-4 text-left ${
                      isSelected
                        ? 'border-accent bg-accent/5'
                        : 'border-rule bg-raise hover:border-accent/40'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink flex items-center gap-1.5">
                        {item.label}
                        {isSelected && <CheckCircle2 className="size-4 text-accent shrink-0" />}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Card 2: Đánh giá trải nghiệm */}
          <div className="rounded-xl bg-raise p-5 sm:p-6 space-y-3 shadow-card">
            <div>
              <h2 className="text-base font-semibold text-ink">Mức hài lòng</h2>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isFilled = (hoverRating ?? rating) >= star
                  return (
                    <button
                      key={star}
                      type="button"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(null)}
                      onClick={() => setRating(star)}
                      className="p-1 text-warn hover:scale-115 transition-transform focus:outline-none"
                    >
                      <Star
                        className={`size-7 sm:size-8 ${
                          isFilled ? 'fill-warn text-warn' : 'text-muted '
                        }`}
                      />
                    </button>
                  )
                })}
              </div>
              <span className="text-xs sm:text-sm font-medium text-warn px-2.5 py-1 rounded-md bg-warn/12  border border-warn/60">
                {STAR_LABELS[hoverRating ?? rating]} ({hoverRating ?? rating}/5 sao)
              </span>
            </div>
          </div>

          {/* Card 3: Chi tiết góp ý */}
          <div className="rounded-xl bg-raise p-5 sm:p-6 space-y-4 shadow-card">
            <div>
              <h2 className="text-base font-semibold text-ink">Nội dung</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="feedback-title" className="block text-xs font-semibold text-inkSoft mb-1.5">
                  Tiêu đề <span className="text-danger">*</span>
                </label>
                <Input
                  id="feedback-title"
                  required
                  maxLength={200}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    category === 'bug'
                      ? 'VD: Lỗi lọc đơn theo ngày'
                      : category === 'feature'
                      ? 'VD: Tạo nhiều đơn từ Excel'
                      : undefined
                  }
                  className="bg-paper"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="feedback-content" className="block text-xs font-semibold text-inkSoft">
                    Nội dung chi tiết <span className="text-danger">*</span>
                  </label>
                  <span className="text-xs text-muted font-mono">{content.length}/5000</span>
                </div>
                <textarea
                  id="feedback-content"
                  required
                  rows={5}
                  maxLength={5000}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full rounded-md border border-rule bg-paper p-3 text-base sm:text-sm text-ink placeholder:text-muted focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20"
                />
              </div>
            </div>
          </div>

          {/* Card 4: Thông tin liên hệ */}
          <div className="rounded-xl bg-raise p-5 sm:p-6 space-y-4 shadow-card">
            <div>
              <h2 className="text-base font-semibold text-ink">Người gửi</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-inkSoft mb-1.5">Tên Shop / người gửi</label>
                <Input
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Shop chinSu"
                  className="bg-paper"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-inkSoft mb-1.5">Số điện thoại</label>
                <Input
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                  placeholder="0912 345 678"
                  className="bg-paper"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-inkSoft mb-1.5">Email</label>
                <Input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="bg-paper"
                />
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-2">
            <Button
              type="submit"
              disabled={submitMutation.isPending || !title.trim() || !content.trim()}
              className="w-full sm:w-auto px-8 gap-2 shadow-none"
            >
              <span>{submitMutation.isPending ? 'Đang gửi…' : 'Gửi'}</span>
            </Button>
          </div>
        </form>
      ) : tab === 'downline' ? (
        <DownlineTickets focusId={focusId} />
      ) : (
        /* Tab Lịch sử phản hồi */
        <div className="rounded-xl bg-raise p-5 sm:p-6 space-y-4 shadow-card">
          <div className="flex items-center justify-between pb-3 border-b border-rule">
            <div>
              <h2 className="text-base font-semibold text-ink">Đã gửi</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTab('form')}
              className="gap-1.5 text-xs"
            >
              <span>Gửi góp ý</span>
            </Button>
          </div>

          {loadingHistory ? (
            <div className="py-12 text-center text-sm text-muted">
              Đang tải…
            </div>
          ) : !historyData?.items || historyData.items.length === 0 ? (
            <div className="py-12 text-center space-y-3">
              <div className="size-12 rounded-full bg-accent/10 text-accent flex items-center justify-center mx-auto">
              </div>
              <p className="text-sm font-medium text-ink">Chưa có góp ý</p>
              <Button size="sm" onClick={() => setTab('form')} className="mt-2">
                Gửi góp ý
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-rule">
              {historyData.items.map((item, i) => {
                const catInfo = CATEGORIES.find((c) => c.id === item.category) ?? CATEGORIES[0]!
                return (
                  <div
                    key={item.id}
                    id={`feedback-${item.id}`}
                    className={`py-4 space-y-2 first:pt-0 last:pb-0 ${item.id === focusId ? 'rounded-lg px-2 ring-2 ring-accent/40' : ''}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <IndexBadge n={i + 1} />
                        <span className="inline-flex items-center gap-1 rounded-full border border-rule bg-paperSoft px-2.5 py-0.5 text-xs text-inkSoft">
                          <span>{catInfo.label}</span>
                        </span>
                        {item.rating && (
                          <span className="text-xs text-warn font-medium flex items-center gap-0.5">
                            {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{formatDateTime(item.createdAt)}</span>
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-accent/12 text-accent">
                          {TICKET_STATUS_LABEL[item.status] ?? item.status}
                        </span>
                      </div>
                    </div>

                    <h3 className="text-sm font-semibold text-ink">{item.title}</h3>
                    <p className="text-xs text-inkSoft whitespace-pre-wrap leading-relaxed bg-paper/60 p-3 rounded-lg border border-rule/60">
                      {item.content}
                    </p>
                    <TicketHandling t={item} />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
